import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Database,
  Download,
  HardDrive,
  RefreshCw,
  Clock,
  Calendar,
  Trash2,
  Play,
  Save,
  FolderOpen,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  Package,
  ShieldCheck,
  Code2,
  FileArchive,
  Settings2,
  Lock,
  Unlock,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const FREQUENCIES = [
  { value: 'daily', label: 'Ogni giorno' },
  { value: 'weekly', label: 'Ogni settimana' },
  { value: 'monthly', label: 'Ogni mese' },
];

const DAYS_OF_WEEK = [
  { v: 0, label: 'Lunedì' },
  { v: 1, label: 'Martedì' },
  { v: 2, label: 'Mercoledì' },
  { v: 3, label: 'Giovedì' },
  { v: 4, label: 'Venerdì' },
  { v: 5, label: 'Sabato' },
  { v: 6, label: 'Domenica' },
];

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(Math.max(bytes, 1)) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' });
  } catch (e) {
    return iso;
  }
};

export default function BackupRestore() {
  const [schedule, setSchedule] = useState({
    enabled: false,
    frequency: 'daily',
    hour: 2,
    minute: 0,
    day_of_week: 0,
    day_of_month: 1,
    retention_count: 7,
    backup_path: '',
    include_database: true,
    include_documents: true,
    include_code: true,
    include_config: true,
    last_run: null,
    last_run_result: null,
    last_run_error: null,
    active_backup_path: null,
    using_default_path: true,
  });
  const [backups, setBackups] = useState([]);
  const [encryptionConfigured, setEncryptionConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [pathTest, setPathTest] = useState(null);
  const [testingPath, setTestingPath] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [resSched, resList] = await Promise.all([
        axios.get(`${API}/api/backup/schedule`),
        axios.get(`${API}/api/backup/list`),
      ]);
      setSchedule((s) => ({ ...s, ...resSched.data }));
      setBackups(resList.data.backups || []);
      setEncryptionConfigured(resList.data.encryption_configured !== false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore caricamento configurazione');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: schedule.enabled,
        frequency: schedule.frequency,
        hour: parseInt(schedule.hour),
        minute: parseInt(schedule.minute),
        day_of_week: parseInt(schedule.day_of_week),
        day_of_month: parseInt(schedule.day_of_month),
        retention_count: parseInt(schedule.retention_count) || 7,
        backup_path: (schedule.backup_path || '').trim(),
        include_database: schedule.include_database,
        include_documents: schedule.include_documents,
        include_code: schedule.include_code,
        include_config: schedule.include_config,
      };
      await axios.put(`${API}/api/backup/schedule`, payload);
      toast.success(schedule.enabled ? 'Backup automatico ATTIVO' : 'Configurazione salvata (backup disattivato)');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const testBackupPath = async () => {
    const p = (schedule.backup_path || '').trim();
    if (!p) {
      setPathTest({ valid: false, error: 'Inserisci un path da testare' });
      return;
    }
    setTestingPath(true);
    setPathTest(null);
    try {
      const res = await axios.post(`${API}/api/backup/test-path`, { path: p });
      setPathTest(res.data);
      if (!res.data.valid) toast.error(res.data.error || 'Cartella non valida');
      else toast.success('Cartella valida e scrivibile');
    } catch (e) {
      setPathTest({ valid: false, error: e.response?.data?.detail || 'Errore' });
    } finally {
      setTestingPath(false);
    }
  };

  const runNow = async () => {
    if (!window.confirm('Eseguire ora un backup totale del sistema (DB + documenti + codice + config)? Potrebbe richiedere alcuni minuti.')) return;
    setRunning(true);
    try {
      const res = await axios.post(`${API}/api/backup/run-now`);
      toast.success(
        `Backup completato: ${res.data.filename} (${formatBytes(res.data.size_bytes)})` +
        (res.data.encrypted ? ' — cifrato e verificato' : ' — NON cifrato')
      );
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel backup');
    } finally {
      setRunning(false);
    }
  };

  const downloadBackup = async (filename) => {
    try {
      const res = await axios.get(`${API}/api/backup/download/${filename}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Errore nel download');
    }
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Cancellare definitivamente "${filename}"?`)) return;
    try {
      await axios.delete(`${API}/api/backup/${filename}`);
      toast.success('Backup cancellato');
      fetchAll();
    } catch (e) {
      toast.error('Errore nella cancellazione');
    }
  };

  if (loading) {
    return (
      <div className="p-8 max-w-5xl mx-auto">
        <div className="flex items-center gap-3 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Caricamento configurazione…</span>
        </div>
      </div>
    );
  }

  const totalSize = backups.reduce((acc, b) => acc + (b.size_bytes || 0), 0);

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto space-y-6" data-testid="backup-page">
      {/* HEADER */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900 flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-violet-100 flex items-center justify-center">
              <Package className="w-6 h-6 text-violet-700" />
            </div>
            Backup Totale Sistema
          </h1>
          <p className="text-sm text-slate-500 mt-1 ml-14">
            Database (BSON) + Documenti + Codice + Configurazioni — un singolo archivio <code className="px-1 py-0.5 bg-slate-100 rounded text-xs">.tar.gz</code> programmabile
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      {/* CARD CONFIGURAZIONE */}
      <Card id="schedule-section" className="border-violet-200 scroll-mt-24">
        <CardContent className="p-6 space-y-5">
          {/* Header card */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-violet-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Backup Automatico Programmato</h2>
                <p className="text-xs text-slate-500">Esecuzione automatica nei tempi definiti</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Label htmlFor="auto-enabled" className="text-sm text-slate-600">Attivo</Label>
              <Switch
                id="auto-enabled"
                checked={schedule.enabled}
                onCheckedChange={(v) => setSchedule((s) => ({ ...s, enabled: v }))}
                data-testid="schedule-enabled-switch"
              />
            </div>
          </div>

          {/* Frequenza + orario */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <Label className="text-xs text-slate-600">Frequenza</Label>
              <Select
                value={schedule.frequency}
                onValueChange={(v) => setSchedule((s) => ({ ...s, frequency: v }))}
              >
                <SelectTrigger data-testid="schedule-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.map((f) => (
                    <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {schedule.frequency === 'weekly' && (
              <div>
                <Label className="text-xs text-slate-600">Giorno settimana</Label>
                <Select
                  value={String(schedule.day_of_week)}
                  onValueChange={(v) => setSchedule((s) => ({ ...s, day_of_week: parseInt(v) }))}
                >
                  <SelectTrigger data-testid="schedule-dow-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((d) => (
                      <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {schedule.frequency === 'monthly' && (
              <div>
                <Label className="text-xs text-slate-600">Giorno del mese</Label>
                <Input
                  type="number"
                  min="1"
                  max="28"
                  value={schedule.day_of_month}
                  onChange={(e) => setSchedule((s) => ({ ...s, day_of_month: parseInt(e.target.value) || 1 }))}
                  data-testid="schedule-dom-input"
                />
              </div>
            )}

            <div>
              <Label className="text-xs text-slate-600">Ora</Label>
              <Input
                type="number"
                min="0"
                max="23"
                value={schedule.hour}
                onChange={(e) => setSchedule((s) => ({ ...s, hour: e.target.value }))}
                data-testid="schedule-hour-input"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-600">Minuti</Label>
              <Input
                type="number"
                min="0"
                max="59"
                value={schedule.minute}
                onChange={(e) => setSchedule((s) => ({ ...s, minute: e.target.value }))}
                data-testid="schedule-minute-input"
              />
            </div>

            <div>
              <Label className="text-xs text-slate-600">Backup da conservare</Label>
              <Input
                type="number"
                min="1"
                value={schedule.retention_count}
                onChange={(e) => setSchedule((s) => ({ ...s, retention_count: e.target.value }))}
                data-testid="schedule-retention-input"
              />
            </div>
          </div>

          {/* Componenti da includere */}
          <div>
            <Label className="text-xs text-slate-600 mb-2 block">Componenti da includere nel backup</Label>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                <Checkbox
                  id="inc-db"
                  checked={schedule.include_database}
                  onCheckedChange={(v) => setSchedule((s) => ({ ...s, include_database: !!v }))}
                  data-testid="inc-db-checkbox"
                />
                <Label htmlFor="inc-db" className="cursor-pointer flex items-center gap-2 text-sm">
                  <Database className="w-4 h-4 text-emerald-600" />
                  Database (BSON)
                </Label>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                <Checkbox
                  id="inc-docs"
                  checked={schedule.include_documents}
                  onCheckedChange={(v) => setSchedule((s) => ({ ...s, include_documents: !!v }))}
                  data-testid="inc-docs-checkbox"
                />
                <Label htmlFor="inc-docs" className="cursor-pointer flex items-center gap-2 text-sm">
                  <FileArchive className="w-4 h-4 text-amber-600" />
                  Documenti caricati
                </Label>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                <Checkbox
                  id="inc-code"
                  checked={schedule.include_code}
                  onCheckedChange={(v) => setSchedule((s) => ({ ...s, include_code: !!v }))}
                  data-testid="inc-code-checkbox"
                />
                <Label htmlFor="inc-code" className="cursor-pointer flex items-center gap-2 text-sm">
                  <Code2 className="w-4 h-4 text-blue-600" />
                  Codice (backend/frontend)
                </Label>
              </div>
              <div className="flex items-center gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50/50">
                <Checkbox
                  id="inc-config"
                  checked={schedule.include_config}
                  onCheckedChange={(v) => setSchedule((s) => ({ ...s, include_config: !!v }))}
                  data-testid="inc-config-checkbox"
                />
                <Label htmlFor="inc-config" className="cursor-pointer flex items-center gap-2 text-sm">
                  <Settings2 className="w-4 h-4 text-violet-600" />
                  Configurazioni
                </Label>
              </div>
            </div>
          </div>

          {/* Cartella destinazione */}
          <div>
            <Label className="text-xs text-slate-600 flex items-center gap-1">
              <FolderOpen className="w-3 h-3" /> Cartella di destinazione (locale o NAS montato)
            </Label>
            <div className="flex gap-2">
              <Input
                placeholder="Lascia vuoto per cartella interna del CRM, oppure es. /mnt/nas-backup"
                value={schedule.backup_path || ''}
                onChange={(e) => setSchedule((s) => ({ ...s, backup_path: e.target.value }))}
                className="flex-1 font-mono text-sm"
                data-testid="backup-path-input"
              />
              <Button
                onClick={testBackupPath}
                disabled={testingPath}
                variant="outline"
                data-testid="test-path-btn"
              >
                {testingPath ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
                Testa Cartella
              </Button>
            </div>
            {pathTest && (
              <div className={`mt-2 p-2 rounded-lg text-sm flex items-start gap-2 ${pathTest.valid ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800'}`}>
                {pathTest.valid ? <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" /> : <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />}
                <div>
                  {pathTest.valid
                    ? <>{pathTest.message}. <span className="text-xs">Spazio libero: <strong>{pathTest.free_space_gb} GB</strong></span></>
                    : pathTest.error}
                </div>
              </div>
            )}
            <p className="text-xs text-slate-500 mt-1">
              💡 Attualmente in uso: <code className="px-1 py-0.5 bg-slate-100 rounded">{schedule.active_backup_path}</code>
              {schedule.using_default_path && <span className="ml-1 text-slate-400">(default)</span>}
            </p>
          </div>

          {/* Footer: ultimo run + bottoni */}
          <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-slate-200">
            <div className="text-xs text-slate-500 space-y-0.5">
              <div>
                Ultimo backup:{' '}
                {schedule.last_run
                  ? <strong className="text-slate-700">{formatDate(schedule.last_run)}</strong>
                  : <em>mai eseguito</em>}
              </div>
              {schedule.last_run_error && (
                <div className="text-rose-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Ultimo errore: {schedule.last_run_error}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={runNow}
                disabled={running}
                variant="outline"
                size="sm"
                data-testid="run-now-btn"
              >
                {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Play className="w-4 h-4 mr-2" />}
                Esegui Ora
              </Button>
              <Button
                onClick={saveConfig}
                disabled={saving}
                size="sm"
                className="bg-violet-600 hover:bg-violet-700"
                data-testid="save-schedule-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Salva Configurazione
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* CARD LISTA BACKUP */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-slate-900">Backup disponibili</h2>
                <p className="text-xs text-slate-500 font-mono">{schedule.active_backup_path}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-slate-600">
                {backups.length} {backups.length === 1 ? 'file' : 'file'} · {formatBytes(totalSize)}
              </Badge>
            </div>
          </div>

          {encryptionConfigured ? (
            <div className="mb-4 flex items-center gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
              <Lock className="w-4 h-4" /> I backup vengono cifrati a riposo (AES-256) e verificati alla creazione.
            </div>
          ) : (
            <div className="mb-4 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>
                <strong>Backup NON cifrati.</strong> Contengono database, documenti dei clienti e credenziali.
                Impostare <code className="font-mono">BACKUP_ENC_KEY</code> in <code className="font-mono">backend/.env</code> e riavviare.
              </span>
            </div>
          )}

          {backups.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-40" />
              <p>Nessun backup disponibile</p>
              <p className="text-xs mt-1">Clicca "Esegui Ora" per crearne uno</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="backups-list">
              {backups.map((b) => (
                <div
                  key={b.filename}
                  className="flex items-center justify-between p-3 rounded-lg border border-slate-200 hover:border-violet-300 hover:bg-violet-50/30 transition-colors"
                  data-testid={`backup-row-${b.filename}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileArchive className="w-5 h-5 text-violet-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-medium text-slate-800 truncate">{b.filename}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2">
                        <Calendar className="w-3 h-3" /> {formatDate(b.created_at)}
                        <span>·</span>
                        <span>{formatBytes(b.size_bytes)}</span>
                        <span>·</span>
                        {b.encrypted
                          ? <span className="inline-flex items-center gap-1 text-emerald-600"><Lock className="w-3 h-3" /> cifrato</span>
                          : <span className="inline-flex items-center gap-1 text-amber-600"><Unlock className="w-3 h-3" /> in chiaro</span>}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      onClick={() => downloadBackup(b.filename)}
                      size="sm"
                      variant="ghost"
                      data-testid={`download-${b.filename}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => deleteBackup(b.filename)}
                      size="sm"
                      variant="ghost"
                      className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                      data-testid={`delete-${b.filename}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* CARD INFO RIPRISTINO */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-900 space-y-2">
              <p className="font-semibold">📋 Come ripristinare un backup</p>
              <p>Il ripristino si fa <strong>solo da terminale Ubuntu</strong> per sicurezza:</p>
              <pre className="bg-white border border-blue-200 p-3 rounded text-xs overflow-x-auto font-mono">
{`# 1) Scarica il backup dalla lista qui sopra (o copialo da /mnt/nas-backup)
# 2) Estrai e lancia lo script di restore incluso:

tar -xzf jvservice_backup_*.tar.gz -C /tmp/
cd /tmp/jvservice_backup_*/
bash restore.sh                  # ripristino totale guidato
bash restore.sh db               # solo database
bash restore.sh uploads          # solo documenti
bash restore.sh code             # solo codice
bash restore.sh config           # solo configurazioni`}
              </pre>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
